import React from 'react'
import { Logo } from '@/components/Logo'
import { ModeToggle } from '@/components/ModeToggle'

interface AuthLayoutProps {
  children: React.ReactNode
  title?: string
  description?: string
}

export function AuthLayout({ children, title, description }: AuthLayoutProps) {
  return (
    <div className="flex min-h-svh">
      {/* Left panel — brand gradient, hidden on mobile */}
      <div className="hidden md:flex md:w-2/5 flex-col items-center justify-center bg-gradient-to-br from-primary to-accent p-10 relative overflow-hidden">
        {/* Decorative background shapes */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-20 -left-20 h-64 w-64 rounded-full bg-primary-foreground/10" />
          <div className="absolute top-1/3 -right-16 h-48 w-48 rounded-full bg-primary-foreground/10" />
          <div className="absolute bottom-10 left-1/4 h-32 w-32 rounded-full bg-primary-foreground/10" />
          <div className="absolute bottom-1/4 right-1/3 h-16 w-16 rounded-full bg-primary-foreground/10" />
        </div>

        {/* Logo */}
        <div className="relative z-10 flex flex-col items-center gap-6">
          <Logo variant="full" className="h-12 w-auto brightness-0 invert" />

          {/* Abstract illustration */}
          <div className="flex gap-3 mt-4">
            <div className="h-16 w-3 rounded-full bg-primary-foreground/20" />
            <div className="h-24 w-3 rounded-full bg-primary-foreground/30 mt-4" />
            <div className="h-20 w-3 rounded-full bg-primary-foreground/25 mt-2" />
            <div className="h-12 w-3 rounded-full bg-primary-foreground/20 mt-6" />
            <div className="h-28 w-3 rounded-full bg-primary-foreground/35" />
            <div className="h-18 w-3 rounded-full bg-primary-foreground/20 mt-3" />
          </div>

          {/* Tagline */}
          <p className="text-primary-foreground/80 text-sm text-center max-w-48 leading-relaxed">
            Gestion des relations donateurs
          </p>
        </div>
      </div>

      {/* Right panel — form area */}
      <div className="flex flex-1 flex-col bg-background">
        {/* Mobile: top bar with logo and mode toggle */}
        <div className="flex items-center justify-between p-4 md:hidden">
          <Logo variant="full" />
          <ModeToggle />
        </div>

        {/* Desktop: mode toggle in top-right corner */}
        <div className="hidden md:flex justify-end p-4">
          <ModeToggle />
        </div>

        {/* Centered form content */}
        <div className="flex flex-1 items-center justify-center px-4 pb-8 md:pb-16">
          <div className="w-full max-w-sm">
            {(title || description) && (
              <div className="mb-6">
                {title && (
                  <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
                )}
                {description && (
                  <p className="mt-1 text-sm text-muted-foreground">{description}</p>
                )}
              </div>
            )}
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}
